/**
 * FreeFormSubmissionsPage.jsx — Bölüm 25-H
 * Free Form — Gönderilen formlar listesi + detay modal.
 * Route: /Management/FreeForm/submissions
 *
 * Filtreler: Şablon, Fuar, Hedef Tipi, Tarih aralığı
 * Detay: Tüm cevaplar + fotoğraf thumbnails
 */

import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Alert, Badge, Button, Card, CardBody,
  Col, Input, Label,
  Modal, ModalBody, ModalHeader, Row, Spinner, Table,
} from "reactstrap";
import {
  Camera, ChevronLeft, ChevronRight,
  Eye, FileText, FolderOpen, Search, X,
} from "lucide-react";
import { PageFrame, PageBody } from "../../../Components/Common/EmbeddablePage";
import TarihGirdisi from "../../../Components/Common/TarihGirdisi";
import {
  getFreeFormPhotoUrl,
  getFreeFormPhotos,
  getSubmission,
  getTemplateTree,
  listSubmissions,
  listTemplates,
  flatFreeFormNodes,
} from "../../../services/freeFormService";
import { fetchFuarList } from "../../../slices/fuarSlice";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

// ─── Sabitler ────────────────────────────────────────────────────────────────

const ENTITY_LABELS = { get FAIR() { return i18n.t("formlar:freeFormSubmissions.fuar"); }, get SALON() { return i18n.t("formlar:freeFormSubmissions.salon"); }, get STAND() { return i18n.t("formlar:freeFormSubmissions.stant"); }, get PERSON() { return i18n.t("formlar:freeFormSubmissions.kisi"); } };
const ENTITY_COLORS = { FAIR: "primary", SALON: "info", STAND: "success", PERSON: "warning" };
const ANSWER_TYPES  = { get TEXT() { return i18n.t("formlar:freeFormSubmissions.metin"); }, get NUMBER() { return i18n.t("formlar:freeFormSubmissions.sayi"); }, get SINGLE_CHOICE() { return i18n.t("formlar:freeFormSubmissions.tekSecim"); }, get MULTI_CHOICE() { return i18n.t("formlar:freeFormSubmissions.cokSecim"); }, get PHOTO() { return i18n.t("formlar:freeFormSubmissions.fotograf"); } };

const PAGE_SIZES = [10, 25, 50];

// ─── Yardımcı: Cevap Gösterimi ───────────────────────────────────────────────

function AnswerDisplay({ answer }) {
  const { t } = useTranslation("formlar");
  const { answerType, textValue, numberValue, selectedChoices } = answer;
  if (answerType === "TEXT")
    return <span className="small">{textValue || <em className="text-muted">{t("freeFormSubmissions.bos")}</em>}</span>;
  if (answerType === "NUMBER")
    return <span className="small fw-semibold">{numberValue ?? <em className="text-muted">—</em>}</span>;
  if (answerType === "SINGLE_CHOICE" || answerType === "MULTI_CHOICE") {
    const choices = selectedChoices || [];
    return choices.length > 0
      ? <div className="d-flex flex-wrap gap-1">{choices.map((c, i) => <Badge key={i} color="primary" pill className="fw-normal">{c}</Badge>)}</div>
      : <em className="text-muted small">{t("freeFormSubmissions.secimYapilmamis")}</em>;
  }
  if (answerType === "PHOTO")
    return <em className="text-muted small">{t("freeFormSubmissions.fotografAyriYuklendi")}</em>;
  return <span className="text-muted small">—</span>;
}

// ─── Yardımcı: Fotoğraf Galerisi (soru bazlı) ────────────────────────────────

function PhotoGallery({ submissionId, questionNodeId, onPreview }) {
  const { t } = useTranslation("formlar");
  const [photos,  setPhotos]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await getFreeFormPhotos(submissionId, questionNodeId);
        setPhotos(res?.data || res || []);
      } catch { setPhotos([]); }
      finally { setLoading(false); }
    }
    load();
  }, [submissionId, questionNodeId]);

  if (loading) return <Spinner size="sm" color="secondary" />;
  if (photos.length === 0) return <em className="text-muted small">{t("freeFormSubmissions.fotografYok")}</em>;

  return (
    <div className="d-flex flex-wrap gap-1 mt-1">
      {photos.map((p, i) => {
        const url = getFreeFormPhotoUrl(p.storedFileName || p.fileName || p.storedName);
        return url ? (
          <button key={i} type="button" className="border-0 bg-transparent p-0" onClick={() => onPreview(url)}>
            <img src={url} alt={t("freeFormSubmissions.foto")} className="rounded" style={{ width: 48, height: 48, objectFit: "cover" }} />
          </button>
        ) : null;
      })}
    </div>
  );
}

// ─── Detay Modal ─────────────────────────────────────────────────────────────

function SubmissionDetailModal({ submissionId, onClose }) {
  const { t } = useTranslation("formlar");
  const [data,      setData]      = useState(null);
  const [nodeMap,   setNodeMap]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [previewUrl,setPreviewUrl]= useState(null);

  useEffect(() => {
    if (!submissionId) return;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const sub  = await getSubmission(submissionId);
        const tId  = sub?.templateId || sub?.data?.templateId;
        setData(sub?.data || sub);

        if (tId) {
          const tree = await getTemplateTree(tId);
          // Backend "rootNodes" key kullanıyor → flatFreeFormNodes ile düzleştir
          const nodes = flatFreeFormNodes(tree);
          const map = {};
          // ID her zaman string key olarak sakla (backend number döndürebilir)
          nodes.forEach((n) => { map[String(n._id || n.id)] = n; });
          setNodeMap(map);
        }
      } catch (err) {
        setError(err?.response?.data?.message || i18n.t("formlar:freeFormSubmissions.detayYuklenemedi"));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [submissionId]);

  const answers   = data?.answers      || [];
  const photoAnss = answers.filter((a) => a.answerType === "PHOTO");
  const textAnss  = answers.filter((a) => a.answerType !== "PHOTO");

  return (
    <Modal isOpen={!!submissionId} toggle={onClose} size="lg" scrollable centered>
      <ModalHeader toggle={onClose}>
        <FileText size={16} className="me-2 text-primary" />
        {t("freeFormSubmissions.submissionDetayi")}
        {data && <span className="text-muted small ms-2">#{data.id || submissionId}</span>}
      </ModalHeader>
      <ModalBody>
        {loading && (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        )}
        {error && <Alert color="danger">{error}</Alert>}
        {!loading && data && (
          <>
            {/* Meta Bilgiler */}
            <div className="bg-light rounded p-3 mb-3">
              <Row className="g-2">
                <Col xs={6} sm={4}>
                  <span className="text-muted small d-block">{t("freeFormSubmissions.hedefTipi")}</span>
                  <Badge color={ENTITY_COLORS[data.targetEntityType] || "secondary"}>
                    {ENTITY_LABELS[data.targetEntityType] || data.targetEntityType || "—"}
                  </Badge>
                </Col>
                <Col xs={6} sm={4}>
                  <span className="text-muted small d-block">{t("freeFormSubmissions.hedefId")}</span>
                  <strong className="small">{data.targetEntityId ?? "—"}</strong>
                </Col>
                <Col xs={6} sm={4}>
                  <span className="text-muted small d-block">{t("freeFormSubmissions.fuarId")}</span>
                  <strong className="small">{data.fuarId ?? "—"}</strong>
                </Col>
                <Col xs={12} sm={6}>
                  <span className="text-muted small d-block">{t("freeFormSubmissions.gonderilme")}</span>
                  <strong className="small">
                    {data.submittedAt
                      ? new Date(data.submittedAt).toLocaleString(currentLocaleTag())
                      : data.createdAt
                      ? new Date(data.createdAt).toLocaleString(currentLocaleTag())
                      : "—"}
                  </strong>
                </Col>
                <Col xs={12} sm={6}>
                  <span className="text-muted small d-block">{t("freeFormSubmissions.sablonId")}</span>
                  <strong className="small">{data.templateId ?? "—"}</strong>
                </Col>
              </Row>
            </div>

            {/* Cevaplar */}
            {textAnss.length > 0 && (
              <div className="mb-3">
                <h6 className="fw-semibold small text-uppercase text-muted mb-2">{t("freeFormSubmissions.cevaplar")}</h6>
                <div className="border rounded" style={{ overflow: "hidden" }}>
                  {textAnss.map((a, i) => {
                    const node = nodeMap[String(a.questionNodeId)];
                    return (
                      <div key={i} className={`p-2 px-3 ${i < textAnss.length - 1 ? "border-bottom" : ""}`}>
                        <div className="d-flex align-items-start gap-2 flex-wrap">
                          <span className="small fw-semibold flex-grow-1 text-dark">
                            {node?.name || t("freeFormSubmissions.soru", { v: i + 1 })}
                          </span>
                          <Badge color="soft-secondary" style={{ fontSize: "0.6rem" }}>
                            {ANSWER_TYPES[a.answerType] || a.answerType}
                          </Badge>
                        </div>
                        <div className="mt-1">
                          <AnswerDisplay answer={a} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Fotoğraf Soruları */}
            {photoAnss.length > 0 && (
              <div className="mb-2">
                <h6 className="fw-semibold small text-uppercase text-muted mb-2">
                  <Camera size={13} className="me-1" />{t("freeFormSubmissions.fotograflar")}
                </h6>
                <div className="border rounded" style={{ overflow: "hidden" }}>
                  {photoAnss.map((a, i) => {
                    const node = nodeMap[String(a.questionNodeId)];
                    return (
                      <div key={i} className={`p-2 px-3 ${i < photoAnss.length - 1 ? "border-bottom" : ""}`}>
                        <span className="small fw-semibold text-dark">
                          {node?.name || t("freeFormSubmissions.fotografSorusu", { v: i + 1 })}
                        </span>
                        <PhotoGallery
                          submissionId={data.id || submissionId}
                          questionNodeId={a.questionNodeId}
                          onPreview={setPreviewUrl}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {answers.length === 0 && (
              <p className="text-muted text-center small py-3">{t("freeFormSubmissions.cevapBulunamadi")}</p>
            )}
          </>
        )}
      </ModalBody>

      {/* Lightbox */}
      {previewUrl && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(0,0,0,0.85)", zIndex: 9999 }}
          onClick={() => setPreviewUrl(null)}
        >
          <button
            type="button"
            className="btn btn-dark btn-sm rounded-circle position-absolute top-0 end-0 m-3"
            style={{ width: 32, height: 32, padding: 0 }}
            onClick={() => setPreviewUrl(null)}
          >
            <X size={14} />
          </button>
          <img
            src={previewUrl}
            alt={t("freeFormSubmissions.buyukGorunum")}
            className="img-fluid rounded"
            style={{ maxHeight: "85vh", maxWidth: "90vw", objectFit: "contain" }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Modal>
  );
}

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────

// embedded: boolean true → sayfa kabuğu kapanır (sekme olarak render edilir)
export default function FreeFormSubmissionsPage({ embedded = false }) {
  const { t: tx } = useTranslation("formlar");
  const dispatch = useDispatch();
  const fuarList = useSelector((s) => s.fuar?.list || []);
  const location = useLocation();

  // URL'den templateId parametresi varsa filtre olarak başlat
  const urlTemplateId = new URLSearchParams(location.search).get("templateId") || "";

  // ── Filtreler ─────────────────────────────────────────────────────────────
  const [filterTemplate,   setFilterTemplate]   = useState(urlTemplateId);
  const [filterFuar,       setFilterFuar]       = useState("");
  const [filterEntityType, setFilterEntityType] = useState("");
  const [filterFrom,       setFilterFrom]       = useState("");
  const [filterTo,         setFilterTo]         = useState("");
  const [search,           setSearch]           = useState("");

  // ── Şablon listesi (filtre dropdown için) ────────────────────────────────
  const [templates,    setTemplates]    = useState([]);
  const [tplLoading,   setTplLoading]   = useState(true);

  // ── Tablo state ───────────────────────────────────────────────────────────
  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(10);
  const [total,        setTotal]        = useState(0);

  // ── Detay ─────────────────────────────────────────────────────────────────
  const [detailId,     setDetailId]     = useState(null);

  // ── Template adı map'i ────────────────────────────────────────────────────
  const [tplMap, setTplMap] = useState({});

  // ── Sayfa başlığı ─────────────────────────────────────────────────────────
  useEffect(() => {
    document.title = i18n.t("formlar:freeFormSubmissions.gonderilenEsnekFormlarFds");
  }, []);

  // ── İlk yüklemeler ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!fuarList.length) dispatch(fetchFuarList({ page: 0, size: 100 }));
    async function loadTemplates() {
      setTplLoading(true);
      try {
        const res = await listTemplates({ limit: 200 });
        const rawList = res?.records ?? res?.data?.content ?? res?.content ?? res?.data ?? res;
        const list = Array.isArray(rawList) ? rawList : [];
        setTemplates(list);
        const map = {};
        list.forEach((t) => { map[t.id || t._id] = t.name; });
        setTplMap(map);
      } catch { setTemplates([]); }
      finally { setTplLoading(false); }
    }
    loadTemplates();
  }, [dispatch, fuarList.length]);

  // ── Veri yükleme ──────────────────────────────────────────────────────────
  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page - 1,
        limit: pageSize,
        ...(filterTemplate   && { templateId: filterTemplate }),
        ...(filterFuar       && { fuarId:     filterFuar }),
        ...(filterEntityType && { targetEntityType: filterEntityType }),
        ...(filterFrom       && { from: filterFrom }),
        ...(filterTo         && { to:   filterTo }),
      };
      const res = await listSubmissions(params);
      // API bir obje ya da array dönebilir — her koşulda array garantisi
      const rawContent = res?.records ?? res?.data?.content ?? res?.content ?? res?.data ?? res;
      const content = Array.isArray(rawContent) ? rawContent : [];
      const tot     = res?.total ?? res?.data?.totalElements ?? res?.totalElements ?? content.length;
      setRows(content);
      setTotal(tot);
    } catch (err) {
      setError(err?.response?.data?.message || i18n.t("formlar:freeFormSubmissions.verilerYuklenemedi"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterTemplate, filterFuar, filterEntityType, filterFrom, filterTo]);

  useEffect(() => {
    async function run() { await loadSubmissions(); }
    run();
  }, [loadSubmissions]);

  // ── Filtrelenmiş satırlar (client-side search) ────────────────────────────
  const safeRows = Array.isArray(rows) ? rows : [];
  const filteredRows = search.trim()
    ? safeRows.filter((r) => {
        const tplName = tplMap[r.templateId] || "";
        return (
          tplName.toLowerCase().includes(search.toLowerCase()) ||
          String(r.targetEntityId || "").includes(search) ||
          String(r.id || "").includes(search)
        );
      })
    : safeRows;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <PageFrame embedded={embedded}>
      <PageBody embedded={embedded} title={tx("freeFormSubmissions.gonderilenEsnekFormlar")} pageTitle={tx("freeFormSubmissions.freeForm")}>

        {/* ── Filtre Kartı ──────────────────────────────────────────────── */}
        <Card className="border-0 shadow-sm mb-3">
          <CardBody className="py-3">
            <Row className="g-2 align-items-end">
              {/* Şablon Filtresi */}
              <Col xs={12} sm={6} md={3}>
                <Label className="form-label small fw-semibold mb-1">{tx("freeFormSubmissions.sablon")}</Label>
                {tplLoading ? (
                  <Input bsSize="sm" disabled placeholder={tx("freeFormSubmissions.yukleniyor")} />
                ) : (
                  <Input type="select" bsSize="sm" value={filterTemplate} onChange={(e) => { setFilterTemplate(e.target.value); setPage(1); }}>
                    <option value="">{tx("freeFormSubmissions.tumSablonlar")}</option>
                    {templates.map((t) => (
                      <option key={t.id || t._id} value={t.id || t._id}>{t.name}</option>
                    ))}
                  </Input>
                )}
              </Col>

              {/* Fuar Filtresi */}
              <Col xs={12} sm={6} md={2}>
                <Label className="form-label small fw-semibold mb-1">{tx("freeFormSubmissions.fuar")}</Label>
                {fuarList.length > 0 ? (
                  <Input type="select" bsSize="sm" value={filterFuar} onChange={(e) => { setFilterFuar(e.target.value); setPage(1); }}>
                    <option value="">{tx("freeFormSubmissions.tumu")}</option>
                    {fuarList.map((f) => (
                      <option key={f.fuarId} value={f.fuarId}>{f.fuarName || tx("freeFormSubmissions.fuar2", { fuarId: f.fuarId })}</option>
                    ))}
                  </Input>
                ) : (
                  <Input bsSize="sm" type="number" value={filterFuar} placeholder={tx("freeFormSubmissions.fuarId")} onChange={(e) => { setFilterFuar(e.target.value); setPage(1); }} />
                )}
              </Col>

              {/* Hedef Tipi */}
              <Col xs={6} sm={4} md={2}>
                <Label className="form-label small fw-semibold mb-1">{tx("freeFormSubmissions.hedefTipi")}</Label>
                <Input type="select" bsSize="sm" value={filterEntityType} onChange={(e) => { setFilterEntityType(e.target.value); setPage(1); }}>
                  <option value="">{tx("freeFormSubmissions.tumu")}</option>
                  {Object.entries(ENTITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Input>
              </Col>

              {/* Tarih Aralığı */}
              <Col xs={6} sm={4} md={2}>
                <Label className="form-label small fw-semibold mb-1">{tx("freeFormSubmissions.baslangic")}</Label>
                <TarihGirdisi value={filterFrom} onChange={(v) => { setFilterFrom(v); setPage(1); }} />
              </Col>
              <Col xs={6} sm={4} md={2}>
                <Label className="form-label small fw-semibold mb-1">{tx("freeFormSubmissions.bitis")}</Label>
                <TarihGirdisi value={filterTo} onChange={(v) => { setFilterTo(v); setPage(1); }} />
              </Col>

              {/* Temizle */}
              <Col xs={6} sm={4} md={1} className="d-flex align-items-end">
                <Button
                  color="light"
                  size="sm"
                  className="w-100"
                  onClick={() => {
                    setFilterTemplate(""); setFilterFuar(""); setFilterEntityType("");
                    setFilterFrom(""); setFilterTo(""); setSearch(""); setPage(1);
                  }}
                >
                  <X size={13} className="me-1" />{tx("freeFormSubmissions.temizle")}
                </Button>
              </Col>
            </Row>

            {/* Arama */}
            <div className="mt-2">
              <div className="input-group input-group-sm" style={{ maxWidth: 340 }}>
                <span className="input-group-text bg-white"><Search size={13} /></span>
                <Input bsSize="sm" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tx("freeFormSubmissions.sablonAdiHedefIdVeya")} />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* ── Hata ──────────────────────────────────────────────────────── */}
        {error && (
          <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>
        )}

        {/* ── Tablo ─────────────────────────────────────────────────────── */}
        <Card className="border-0 shadow-sm">
          <CardBody className="p-0">
            {/* Üst bar */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
              <span className="small text-muted">
                {loading ? tx("freeFormSubmissions.yukleniyor") : tx("freeFormSubmissions.kayit", { total })}
              </span>
              <div className="d-flex align-items-center gap-2">
                <Label className="form-label mb-0 small">{tx("freeFormSubmissions.sayfaBoyutu")}</Label>
                <Input type="select" bsSize="sm" style={{ width: 70 }} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                  {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Input>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : filteredRows.length === 0 ? (
              <div className="text-center py-5">
                <FolderOpen size={40} className="text-muted mb-2" />
                <p className="text-muted small mb-0">{tx("common:kayitYok")}</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th className="small fw-semibold py-2 px-3">#</th>
                      <th className="small fw-semibold py-2">{tx("freeFormSubmissions.sablon")}</th>
                      <th className="small fw-semibold py-2">{tx("freeFormSubmissions.hedefTipi")}</th>
                      <th className="small fw-semibold py-2">{tx("freeFormSubmissions.hedefId")}</th>
                      <th className="small fw-semibold py-2">{tx("freeFormSubmissions.fuar")}</th>
                      <th className="small fw-semibold py-2">{tx("freeFormSubmissions.tarih")}</th>
                      <th className="small fw-semibold py-2 text-center">{tx("freeFormSubmissions.islem")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, idx) => {
                      const subId = r.id || r._id;
                      const tplName = tplMap[r.templateId] || (r.templateName) || tx("freeFormSubmissions.sablon2", { v: r.templateId || "?" });
                      const dateVal = r.submittedAt || r.createdAt;
                      return (
                        <tr key={subId || idx}>
                          <td className="px-3 small text-muted">
                            {(page - 1) * pageSize + idx + 1}
                          </td>
                          <td>
                            <span className="small fw-semibold">{tplName}</span>
                            <br />
                            <span className="text-muted" style={{ fontSize: "0.68rem" }}>
                              {subId ? `#${String(subId).slice(-8)}` : ""}
                            </span>
                          </td>
                          <td>
                            {r.targetEntityType && (
                              <Badge color={ENTITY_COLORS[r.targetEntityType] || "secondary"} style={{ fontSize: "0.68rem" }}>
                                {ENTITY_LABELS[r.targetEntityType] || r.targetEntityType}
                              </Badge>
                            )}
                          </td>
                          <td className="small">{r.targetEntityId ?? "—"}</td>
                          <td className="small">{r.fuarId ?? "—"}</td>
                          <td className="small text-nowrap">
                            {dateVal
                              ? new Date(dateVal).toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" })
                              : "—"}
                          </td>
                          <td className="text-center">
                            <Button
                              color="soft-primary"
                              size="sm"
                              onClick={() => setDetailId(subId)}
                              title={tx("freeFormSubmissions.detayiGor")}
                            >
                              <Eye size={14} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Sayfalama */}
            {!loading && totalPages > 1 && (
              <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
                <span className="small text-muted">
                  {tx("freeFormSubmissions.sayfa", { page, totalPages })}
                </span>
                <div className="d-flex gap-1">
                  <Button size="sm" color="light" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    <ChevronLeft size={14} />
                  </Button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                    return (
                      <Button key={p} size="sm" color={p === page ? "primary" : "light"} onClick={() => setPage(p)}>
                        {p}
                      </Button>
                    );
                  })}
                  <Button size="sm" color="light" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    <ChevronRight size={14} />
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </PageBody>

      {/* ── Detay Modal ────────────────────────────────────────────────── */}
      {detailId && (
        <SubmissionDetailModal
          submissionId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}
    </PageFrame>
  );
}









